import { ResponseResult } from '@amzn/gbsa-helios-typescript-client';

/**
 * Duck-typed subset of {@link Question} and {@link SubQuestion} that
 * exposes the two mandatory-comment flags. Accepting the shared
 * shape lets one helper serve both parent-question and sub-question
 * comment fields — those two types carry the same pair of flags but
 * disagree on nearly everything else, so pulling the full types in
 * would force every caller to pick a branch.
 */
export interface CommentRequirementTarget {
  readonly requireCommentOnFail?: boolean | undefined;
  readonly requireCommentOnPass?: boolean | undefined;
}

/**
 * Duck-typed subset of {@link Question} and {@link SubQuestion} that
 * exposes the two mandatory-evidence flags. Same rationale as
 * {@link CommentRequirementTarget} — one helper serves both parent
 * and sub attachment slots.
 */
export interface EvidenceRequirementTarget {
  readonly requireEvidenceOnFail?: boolean | undefined;
  readonly requireEvidenceOnPass?: boolean | undefined;
}

/**
 * Returns true when the target's current result requires a comment
 * per the question / sub-question configuration.
 *
 *   - FAIL result + `requireCommentOnFail` → required
 *   - PASS result + `requireCommentOnPass` → required
 *   - NA result → never required (no requireCommentOnNA flag exists)
 *   - Unanswered (result === undefined) → never required, because
 *     an auditor hasn't committed to any result yet and enforcing
 *     the hint on an empty row would nag the surface at first paint.
 *
 * Pure — no react, no hooks, no side effects. Consumed by the
 * PerformAuditQuestionComment inline hint (renders "Comment
 * required" when this returns true AND the draft is blank) and by
 * page-level close-audit blockers via {@link getQuestionViolations}
 * (which enforces the same rule on the persisted response).
 *
 * Kept as its own small module rather than folded into the
 * `auditClosureValidation` module because the read-shape
 * `getQuestionViolations` operates on the SERVER response, not the
 * in-progress draft. Two callers with different sources of truth
 * read cleaner as two named functions than one union-typed function.
 */
export function isCommentRequired(
  target: CommentRequirementTarget,
  result: ResponseResult | undefined,
): boolean {
  if (result === undefined) return false;
  if (result === ResponseResult.FAIL) return target.requireCommentOnFail === true;
  if (result === ResponseResult.PASS) return target.requireCommentOnPass === true;
  return false;
}

/**
 * Returns true when the target's current result requires photo/file
 * evidence per the question / sub-question configuration. Exact
 * mirror of {@link isCommentRequired} over the evidence flag pair —
 * same result gating (NA and unanswered are never required).
 *
 * Consumed by the perform-audit attachment slots (drives the red
 * asterisk + required note on `MediaAttachments`) and mirrors the
 * rule the close-audit validator enforces on the persisted response.
 */
export function isEvidenceRequired(
  target: EvidenceRequirementTarget,
  result: ResponseResult | undefined,
): boolean {
  if (result === undefined) return false;
  if (result === ResponseResult.FAIL) return target.requireEvidenceOnFail === true;
  if (result === ResponseResult.PASS) return target.requireEvidenceOnPass === true;
  return false;
}
